import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import AppleSignInButton from "@/components/auth/AppleSignInButton";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, Building2, UserRound, Phone } from "lucide-react";

function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return err?.message || fallback;
}

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [state, setState] = useState({
    // Homepage hero signup banner prefills these via query params.
    email: params.get("email") || "",
    password: "",
    full_name: params.get("name") || "",
    // Iter-274 W6: Individual (default) / Business toggle. Business fields
    // are only submitted when the Business tab is active.
    account_type: params.get("type") === "business" ? "business" : "individual",
    // Iter 755 — NZ mobile is mandatory for every new account.
    phone: "",
    business_name: "",
    business_nzbn: "",
    business_phone: "",
    business_address: "",
    // Iter-222 (founder-approved): default-opt-in to Allsale Rewards on
    // signup so new members auto-see the /rewards hub in their menu.
    rewards_optin: true,
    loading: false,
  });
  const [nzbnError, setNzbnError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const isBusiness = state.account_type === "business";

  // Iter 755 — client-side NZ mobile rule (02… / +64 2…); the backend
  // enforces the same with a 400 whose message we show verbatim.
  const NZ_MOBILE_RE = /^(?:\+?64|0)2\d{7,9}$/;

  const submit = async (e) => {
    e.preventDefault();
    setNzbnError("");
    setPhoneError("");
    const phoneDigits = (state.phone || "").replace(/[\s-]/g, "");
    if (!phoneDigits) {
      setPhoneError("A mobile number is required to create an account");
      return;
    }
    if (!NZ_MOBILE_RE.test(phoneDigits)) {
      setPhoneError("Please use a New Zealand mobile number (starts with 02 or +64 2). Allsale Classifieds is NZ-only.");
      return;
    }
    // Client-side gate for the 13-digit NZBN rule. The backend also
    // enforces this (422 "business_nzbn must be 13 digits") — we surface
    // the guidance BEFORE the round-trip so users don't wait.
    if (isBusiness) {
      if (!(state.business_name || "").trim()) {
        toast.error("Business name is required for a business account");
        return;
      }
      // Iter-290 §2 — NZBN, business phone, and business address are
      // now MANDATORY on business signup (backend enforces with 400).
      const digits = (state.business_nzbn || "").replace(/\D/g, "");
      if (digits.length !== 13) {
        setNzbnError("NZBN is required — must be 13 digits.");
        return;
      }
      if (!(state.business_phone || "").trim()) {
        toast.error("Business phone is required for a business account");
        return;
      }
      if (!(state.business_address || "").trim()) {
        toast.error("Business address is required for a business account");
        return;
      }
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const payload = {
        email: state.email,
        password: state.password,
        full_name: state.full_name,
        rewards_optin: state.rewards_optin,
        account_type: state.account_type,
        phone: phoneDigits,
      };
      if (isBusiness) {
        payload.business_name = state.business_name.trim();
        payload.business_nzbn = state.business_nzbn.replace(/\D/g, "");
        payload.business_phone = state.business_phone.trim();
        payload.business_address = state.business_address.trim();
      }
      await register(payload);
      toast.success(isBusiness ? "Your Allsale Business account is ready!" : "Kia Ora — your Allsale account is ready!");
      nav(next, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 && typeof detail === "string" && /mobile number/i.test(detail)) {
        // Iter 755 — show the backend's NZ-mobile message verbatim.
        setPhoneError(detail);
        toast.error(detail);
      } else if (status === 422 && typeof detail === "string" && /nzbn/i.test(detail)) {
        setNzbnError(detail);
        toast.error(detail);
      } else {
        toast.error(errMsg(err, "Registration failed"));
      }
      setState((s) => ({ ...s, loading: false }));
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12">
      <Seo title="Create your account" />
      <div className="rounded-3xl bg-white shadow-card p-8">
        <h1 className="font-display text-3xl font-bold text-slate-900">Create your account</h1>
        <p className="mt-1 text-sm text-slate-500">One login for Allsale marketplace + classifieds.</p>

        {/* Iter-274 W6 — segmented Individual / Business toggle */}
        <div className="mt-5 grid grid-cols-2 gap-2 p-1 rounded-2xl bg-slate-100" role="tablist" aria-label="Account type" data-testid="signup-account-type">
          <button
            type="button"
            role="tab"
            aria-selected={!isBusiness}
            data-testid="signup-type-individual"
            onClick={() => setState((s) => ({ ...s, account_type: "individual" }))}
            className={`inline-flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold transition-colors ${!isBusiness ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            <UserRound className="w-4 h-4" /> Individual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isBusiness}
            data-testid="signup-type-business"
            onClick={() => setState((s) => ({ ...s, account_type: "business" }))}
            className={`inline-flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold transition-colors ${isBusiness ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
          >
            <Building2 className="w-4 h-4" /> Business
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">
          {isBusiness ? "Your ads will show your business name with a 🏢 Business badge." : "Switch to Business anytime later in Account → Edit."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Full name</span>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid={AUTH.nameInput}
                value={state.full_name}
                onChange={(e) => setState({ ...state, full_name: e.target.value })}
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="Jane Doe"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Email</span>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid={AUTH.emailInput}
                value={state.email}
                onChange={(e) => setState({ ...state, email: e.target.value })}
                type="email"
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="you@example.co.nz"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
              NZ mobile number <span className="text-red-500">*</span>
            </span>
            <div className="relative">
              <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-phone"
                value={state.phone}
                onChange={(e) => { setState({ ...state, phone: e.target.value.replace(/[^0-9+ -]/g, "").slice(0, 20) }); if (phoneError) setPhoneError(""); }}
                type="tel"
                inputMode="tel"
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="021 123 4567"
              />
            </div>
            {phoneError ? (
              <p className="mt-1 text-xs text-red-500" data-testid="signup-phone-error">{phoneError}</p>
            ) : (
              <p className="mt-1 text-[11px] text-slate-400">Starts with 02 or +64 2 — Allsale Classifieds is NZ-only.</p>
            )}
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Password</span>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid={AUTH.passwordInput}
                value={state.password}
                onChange={(e) => setState({ ...state, password: e.target.value })}
                type="password"
                minLength={6}
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="At least 6 characters"
              />
            </div>
          </label>

          {isBusiness && (
            <div className="space-y-3 rounded-2xl border border-soft bg-slate-50/50 p-4" data-testid="signup-business-fields">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                  Business name <span className="text-red-500">*</span>
                </span>
                <input
                  data-testid="signup-business-name"
                  value={state.business_name}
                  onChange={(e) => setState({ ...state, business_name: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                  placeholder="Patel Motors Ltd"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">NZBN *</span>
                <input
                  data-testid="signup-business-nzbn"
                  value={state.business_nzbn}
                  onChange={(e) => { setState({ ...state, business_nzbn: e.target.value.replace(/\D/g, "").slice(0, 13) }); if (nzbnError) setNzbnError(""); }}
                  inputMode="numeric"
                  maxLength={13}
                  required
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                  placeholder="9429000000000"
                />
                {nzbnError && <p className="mt-1 text-xs text-red-500" data-testid="signup-business-nzbn-error">{nzbnError}</p>}
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Business phone *</span>
                <input
                  data-testid="signup-business-phone"
                  value={state.business_phone}
                  onChange={(e) => setState({ ...state, business_phone: e.target.value })}
                  inputMode="tel"
                  required
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                  placeholder="09 555 0100"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Business address *</span>
                <input
                  data-testid="signup-business-address"
                  value={state.business_address}
                  onChange={(e) => setState({ ...state, business_address: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                  placeholder="123 Queen Street, Auckland"
                />
              </label>
            </div>
          )}

          <label className="flex items-start gap-2 pt-1 cursor-pointer" data-testid="register-rewards-optin-label">
            <input
              type="checkbox"
              data-testid="register-rewards-optin"
              checked={state.rewards_optin}
              onChange={(e) => setState({ ...state, rewards_optin: e.target.checked })}
              className="mt-0.5 rounded border-soft text-[color:var(--brand-blue)] focus:ring-[color:var(--brand-blue)]"
            />
            <span className="text-sm text-slate-600">
              Join Allsale Rewards — earn points at partner stores, redeem for discounts.{" "}
              <Link
                to="/rewards-terms"
                target="_blank"
                data-testid="register-points-terms-link"
                className="text-[color:var(--brand-blue)] hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                (points terms)
              </Link>
            </span>
          </label>
          <button
            data-testid={AUTH.submit}
            type="submit"
            disabled={state.loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
          >
            {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create account
          </button>
        </form>

        {/* Iter-274d W5 — SSO row under the signup form */}
        <div className="mt-6 relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-soft" />
          </div>
          <div className="relative flex justify-center text-[10px] font-bold uppercase tracking-[0.2em]">
            <span className="bg-white px-3 text-slate-400">or continue with</span>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <GoogleSignInButton nextPath={next} label="Sign up with Google" />
          <AppleSignInButton nextPath={next} label="Sign up with Apple" />
        </div>

        <div className="mt-6 text-sm text-slate-500 text-center">
          Already have an account?{" "}
          <Link data-testid={AUTH.switchBtn} to={`/login${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-semibold text-[color:var(--brand-blue)] hover:underline">
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
}
